import { memo, useEffect, useRef, useState } from 'react';

import { useNavigate } from '@tanstack/react-router';

import { Avatar, AvatarFallback, FormInput, FormSelect } from '@/_components';
import { SectionHeading } from '@/_containers';
import { PriorityBadge } from '@/_containers/support/shared/components/priority-badge';
import {
  priorityOptions,
  statusLabels,
  type SupportTicketSummary,
} from '@/_containers/support/shared/schemas/support-query-schema';
import { nameResolver } from '@/_shared';

type AgentAvatarProps = { src?: string; name?: string };
const AgentAvatar = memo(function AgentAvatar({ src, name }: AgentAvatarProps) {
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const el = imgRef.current;
    if (!el) return;
    if (!src) {
      setLoaded(false);
      return;
    }
    if (el.src !== src) {
      setLoaded(false);
      el.src = src;
    }
  }, [src]);

  return (
    <Avatar>
      {src && (
        <img
          ref={imgRef}
          alt={name || 'Unassigned'}
          className="aspect-square size-full object-cover"
          onLoad={() => setLoaded(true)}
          onError={() => setLoaded(false)}
          loading="lazy"
          decoding="async"
        />
      )}
      {!loaded && <AvatarFallback name={name || 'Unassigned'} />}
    </Avatar>
  );
});

function PropertiesSectionBase({ ticket }: { ticket: SupportTicketSummary }) {
  const { first_name, last_name, profile_image } = ticket?.agent || {};
  const rawSrc = profile_image?.[0]?.url || undefined;
  const nav = useNavigate();

  return (
    <div className="flex flex-col gap-y-2">
      <SectionHeading text="Properties" className="mb-4" />

      {/* Tags */}
      {/* <div>
        <div className="mt-2">
          <CreatableSelect<Option, true>
            isMulti
            placeholder="Add tags"
            value={(value.tags || []).map((t) => ({ value: t, label: t }))}
            onChange={(items) => onChange('tags', items?.map((it) => String(it.value)) || [])}
            disabled
          />
        </div>
      </div> */}

      <FormInput label="Type" value={ticket.type ?? ''} disabled />
      <FormInput label="Status" value={statusLabels[ticket.status]} disabled />
      <FormSelect
        label="Priority"
        value={priorityOptions.find((option) => option.value === ticket.priority.toString()) || priorityOptions[0]}
        options={priorityOptions}
        disabled
        getOptionLabel={(option) => <PriorityBadge priority={option.value as unknown as number} variant="box" />}
        getOptionValue={(option) => option.value}
      />
      <div className="flex w-full items-end">
        <div className="border-c-border-muted border-b pr-1.5 pb-2">
          <AgentAvatar src={rawSrc} name={nameResolver(first_name, last_name, '***UNKNOWN***')} />
        </div>
        <div className="flex-1">
          <FormInput
            containerProps={{
              onClick: () => {
                if (ticket?.agent) {
                  nav({ to: '/team-members/$id', params: { id: `${ticket?.agent?.id || ''}` } });
                }
              },
              className: ticket?.agent ? 'cursor-pointer group' : '',
            }}
            className="decoration-primary decoration-2 disabled:pointer-events-none group-hover:disabled:underline"
            forceHideMessage
            label="Agent"
            value={nameResolver(first_name, last_name, '***UNKNOWN***')}
            disabled
          />
        </div>
      </div>
    </div>
  );
}

export const PropertiesSection = memo(PropertiesSectionBase);
